export type TencentCaptchaResult = {
  ret: number; // 0 = success, 2 = user closed
  ticket?: string;
  randstr?: string;
  errorCode?: number;
  errorMessage?: string;
};

// Element-first signature per the official intl web-integration doc
// (1159/49680) and the console snippet: new TencentCaptcha(DOM, appId, cb, opts).
type TencentCaptchaCtor = new (
  element: HTMLElement,
  appId: string,
  callback: (res: TencentCaptchaResult) => void,
  options?: Record<string, unknown>,
) => { show: () => void; destroy?: () => void };

declare global {
  interface Window {
    TencentCaptcha?: TencentCaptchaCtor;
  }
}

const SCRIPT_SRC = 'https://ca.turing.captcha.qcloud.com/TJNCaptcha-global.js';
let loadPromise: Promise<TencentCaptchaCtor> | null = null;

/** Load the Tencent intl global script once; resolve with the constructor. */
export function loadTencentCaptcha(): Promise<TencentCaptchaCtor> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('no window'));
  }
  if (window.TencentCaptcha) return Promise.resolve(window.TencentCaptcha);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<TencentCaptchaCtor>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      if (window.TencentCaptcha) resolve(window.TencentCaptcha);
      else reject(new Error('TencentCaptcha missing after load'));
    };
    script.onerror = () => {
      loadPromise = null; // allow retry
      reject(new Error('Tencent captcha script failed to load'));
    };
    document.head.appendChild(script);
  });
  return loadPromise;
}
